import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { colorPalette } from "&config/colors";
import { WALEE_BASE_URL } from "&config/url";
import { history } from "&store/store";

const ForgotPasswordComponent = () => {
  const [step, setStep] = useState<"username" | "reset">("username");
  const [userName, setUserName] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckUsername = async () => {
    if (!usernameInput.trim()) {
      toast.error("Username is required", { position: "top-center" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(WALEE_BASE_URL.concat("/auth/forgot-password/check"), {
        userName: usernameInput.trim(),
      });
      setUserName(usernameInput.trim());
      setStep("reset");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to verify username", {
        position: "top-center",
      });
    }
    setLoading(false);
  };

  const handleReset = async () => {
    if (!newPassword || !confirmPassword || !otp) {
      toast.error("All fields are required", { position: "top-center" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match", { position: "top-center" });
      return;
    }
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit code", { position: "top-center" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(WALEE_BASE_URL.concat("/auth/forgot-password/reset"), {
        userName,
        otp,
        newPassword,
      });
      toast.success("Password reset successfully. Please login with your new password.", {
        position: "top-center",
      });
      history.push("/login");
    } catch (err: any) {
      const message = err?.response?.data?.message || "Failed to reset password";
      toast.error(message, { position: "top-center" });
      if (err?.response?.status === 403) {
        history.push("/login");
      } else {
        setOtp("");
      }
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        background: colorPalette.primary,
      }}
    >
      <Card sx={{ padding: "2rem", borderRadius: "19px", width: "441px" }}>
        <CardContent>
          <Box sx={{ textAlign: "center", marginBottom: "2rem" }}>
            <Typography variant="h5" fontWeight="bold" color={colorPalette.primary}>
              Forgot Password
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", marginTop: "0.5rem" }}>
              {step === "username"
                ? "Enter your username to verify your identity."
                : "Enter your new password and the 6-digit code from Google Authenticator."}
            </Typography>
          </Box>

          {step === "username" ? (
            <>
              <TextField
                fullWidth
                label="Username"
                placeholder="Enter your username"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                sx={{ marginBottom: "1.5rem" }}
              />
              <Button
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleCheckUsername}
                sx={{ marginBottom: "1rem", fontWeight: "bold", background: colorPalette.primary }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "CONTINUE"}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => history.push("/login")}
                sx={{ color: "#666" }}
              >
                Back to Login
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label="New Password"
                placeholder="Enter new password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ marginBottom: "1rem" }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                placeholder="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ marginBottom: "1rem" }}
              />
              <TextField
                fullWidth
                label="Authentication Code"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ maxLength: 6 }}
                sx={{ marginBottom: "1.5rem" }}
              />
              <Button
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleReset}
                sx={{ marginBottom: "1rem", fontWeight: "bold", background: colorPalette.primary }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "RESET PASSWORD"}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => setStep("username")}
                sx={{ color: "#666" }}
              >
                Back
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export { ForgotPasswordComponent };
